import { storage } from '@goldblack/client-storage';

// Posts this user liked on this device, stored in the app's localStorage state (key goldblack.loise, path `likes`) as
// { userId, synced, items: { [postId]: expireAt } }. One user at a time: another user logging in on the device starts empty.
// The feed does not read likes from D1 (one query per page was what limited its throughput), so the heart on a feed
// card comes from here. A device that has never loaded the list (new device, cleared storage) gets it once from the
// server (LikeList); after that it only changes through likes made here. The like response and post_get add the
// server's answer. An entry is dropped once its post has expired. Likes made on another device appear when the post is
// opened (post_get) or the list is loaded again.
//
// `current` is the working copy: storage.localStorage.getValue only reflects a write after the next React render, so
// two quick writes would otherwise start from the same stale value.
let current = null;

function load() {
    const userId = storage.transience.getValue('user')?._id ?? null;
    if (!userId) return null;
    if (current?.userId !== userId) {
        const saved = storage.localStorage.getValue('likes');
        current =
            saved?.userId === userId
                ? { userId, synced: !!saved.synced, items: { ...saved.items } }
                : { userId, synced: false, items: {} };
    }
    const now = Date.now();
    const expired = Object.keys(current.items).filter((id) => current.items[id] != null && current.items[id] <= now);
    if (expired.length > 0) {
        expired.forEach((id) => delete current.items[id]);
        save();
    }
    return current;
}

const save = () => storage.localStorage.setState('likes', { ...current, items: { ...current.items } });

const likedPosts = () => load()?.items ?? {};
const likesSynced = () => load()?.synced ?? false;

function setLiked(postId, liked, expireAt) {
    const state = load();
    if (!state || !postId) return;
    if (liked) state.items[postId] = expireAt;
    else delete state.items[postId];
    save();
}

// The server's list, merged under what was liked here meanwhile.
function replaceLikes(list) {
    const state = load();
    if (!state) return;
    state.items = { ...Object.fromEntries(list.map((l) => [l.postId, l.expireAt])), ...state.items };
    state.synced = true;
    save();
}

export { likedPosts, likesSynced, setLiked, replaceLikes };
